import { ArrowLeft } from 'lucide-react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router'

import { DocumentPreviewPane } from '@/components/AgentChat/DocumentPreviewPane'
import { useDocumentPreview } from '@/components/AgentChat/useDocumentPreview'
import { Button } from '@/components/ui/button'
import { useIsBelowLg } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { ChatPanel } from './ChatPanel'
import { ConversationList } from './ConversationList'

/**
 * Top-level route for the internal agent chat.
 *
 * Conversation list on the left, active conversation on the right, and any
 * document the agent filled beside it.
 * Phase 1 ships the plumbing only — no agent backend yet, no message sending.
 */
export function AgentChat() {
  const { id } = useParams<{ id?: string }>()
  const { previewDocuments, setFilledDocuments, pickDocument, closePreview } = useDocumentPreview(id)
  // Below lg this page shows one pane at a time, so there is no room beside the chat.
  const canPreview = !useIsBelowLg()
  const previewOpen = canPreview && previewDocuments.length > 0

  return (
    <>
      <Helmet>
        <title>Internal Agent Chat</title>
      </Helmet>
      {/* Not flex-1: its 0% basis resolves to content height in the min-height shell, so the panes would grow. */}
      <div
        className={cn(
          'mx-auto flex min-h-96 w-full grow basis-0 flex-col',
          // The document gets the extra width; the list and chat keep theirs.
          previewOpen ? 'max-w-[1920px]' : 'max-w-[1200px]'
        )}
      >
        <div className="mb-3 text-2xl font-semibold">Internal Agent Chat</div>
        {/* Two panes side by side on a desktop; on a phone only one of them is on screen at a time. */}
        <div className="flex min-h-0 flex-1 gap-4">
          <div className={cn('w-72 shrink-0 max-lg:w-full', id && 'max-lg:hidden')}>
            <ConversationList activeId={id} />
          </div>
          <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-2', !id && 'max-lg:hidden')}>
            <Button
              variant="outline"
              size="sm"
              href="/insure/internal/agent-chat"
              className="w-fit lg:hidden"
            >
              <ArrowLeft className="size-3.5" />
              All chats
            </Button>
            {/* The panel is h-full, so it needs a parent sized by the row rather than by its own content. */}
            <div className="min-h-0 flex-1">
              <ChatPanel
                conversationId={id}
                onFilledDocuments={canPreview ? setFilledDocuments : undefined}
                onPreviewDocument={canPreview ? pickDocument : undefined}
              />
            </div>
          </div>
          {previewOpen && (
            <DocumentPreviewPane
              docs={previewDocuments}
              onClose={closePreview}
              className="bg-card flex-1 rounded-lg border p-4"
            />
          )}
        </div>
      </div>
    </>
  )
}
